/**
 * @page FlexOnboarding
 * @route /flex/onboarding/:token (public, no auth)
 * @description Public Onboarding-Daten Formular. Bewerber füllt SVNR, IBAN, Adresse, Staatsbürgerschaft, Ausweis-Upload aus.
 *              Bei Drittstaat (nicht EU/EWR/CH): zusätzlich Aufenthaltstitel-Upload Pflicht (#948, §53 AuslBG).
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { COUNTRIES_SORTED, isDrittstaat, getCountryName } from '../data/countries';

const API = import.meta.env.VITE_API_URL || '';

export default function FlexOnboarding() {
  const { token } = useParams();
  const [pageStatus, setPageStatus] = useState('loading');
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [svnr, setSvnr] = useState('');
  const [iban, setIban] = useState('');
  const [adresse, setAdresse] = useState('');
  const [staatsbuergerschaft, setStaatsbuergerschaft] = useState('');
  const [ausweisFile, setAusweisFile] = useState(null);
  const [aufenthaltstitelFile, setAufenthaltstitelFile] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const aufenthaltRef = useRef(null);

  const drittstaat = useMemo(() => isDrittstaat(staatsbuergerschaft), [staatsbuergerschaft]);
  const hasAufenthaltstitelOnServer = !!data?.data?.has_aufenthaltstitel;

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/flex/onboarding/${token}`);
        if (res.status === 404) { setPageStatus('invalid'); return; }
        if (!res.ok) { setPageStatus('error'); return; }
        const d = await res.json();
        setData(d);
        if (d.status === 'completed') setPageStatus('completed');
        else if (d.status === 'expired') setPageStatus('expired');
        else {
          setPageStatus('ready');
          if (d.data) {
            setSvnr(d.data.svnr || '');
            setIban(d.data.iban || '');
            setAdresse(d.data.adresse || '');
            setStaatsbuergerschaft(d.data.staatsbuergerschaft || '');
          }
        }
      } catch { setPageStatus('error'); }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!svnr.trim() || !iban.trim() || !adresse.trim() || !staatsbuergerschaft) {
      setError('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    if (drittstaat && !aufenthaltstitelFile && !hasAufenthaltstitelOnServer) {
      setError('Für die gewählte Staatsbürgerschaft ist ein Aufenthaltstitel verpflichtend.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('svnr', svnr.trim());
      fd.append('iban', iban.trim());
      fd.append('adresse', adresse.trim());
      fd.append('staatsbuergerschaft', staatsbuergerschaft);
      if (ausweisFile) fd.append('ausweis', ausweisFile);
      if (aufenthaltstitelFile) fd.append('aufenthaltstitel', aufenthaltstitelFile);

      const res = await fetch(`${API}/api/public/flex/onboarding/${token}`, {
        method: 'POST',
        body: fd,
      });
      if (res.ok) {
        setPageStatus('completed');
      } else if (res.status === 410) {
        setPageStatus('expired');
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Ein Fehler ist aufgetreten.');
      }
    } catch {
      setError('Netzwerkfehler — bitte versuche es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F4F5FA] to-white flex flex-col">
      {/* Logo bar */}
      <div className="w-full px-6 py-5 flex items-center justify-center">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#338edc] to-[#6366F1] flex items-center justify-center">
            <span className="text-white font-bold text-sm">W</span>
          </div>
          <span className="text-[15px] font-semibold text-[#1A1A2E]">WeShip</span>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 pb-12">
        <div className="w-full max-w-md">

          {pageStatus === 'loading' && (
            <div className="text-center py-20 text-[#9CA3AF]">...</div>
          )}

          {pageStatus === 'invalid' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <div className="text-4xl mb-4">&#128279;</div>
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Dieser Link ist nicht mehr gültig.</h2>
              <p className="text-sm text-[#6B7280]">Bei Fragen: jobs@email.weship.eu</p>
            </div>
          )}

          {pageStatus === 'expired' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <div className="text-4xl mb-4">&#9203;</div>
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Dieser Link ist abgelaufen.</h2>
              <p className="text-sm text-[#6B7280]">Bitte kontaktiere uns unter jobs@email.weship.eu</p>
            </div>
          )}

          {pageStatus === 'error' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Ein Fehler ist aufgetreten.</h2>
              <p className="text-sm text-[#6B7280]">Bitte versuche es später erneut oder kontaktiere jobs@email.weship.eu</p>
            </div>
          )}

          {pageStatus === 'ready' && data && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] overflow-hidden">
              <div className="px-6 pt-6 pb-4">
                <div className="text-4xl mb-4 text-center">&#128221;</div>
                <h1 className="text-xl font-semibold text-[#1A1A2E] text-center">
                  Hallo{data.bewerber_name ? `, ${data.bewerber_name.split(' ')[0]}` : ''}!
                </h1>
                <p className="mt-3 text-sm text-[#6B7280] text-center leading-relaxed">
                  Damit wir dich korrekt anmelden können, brauchen wir noch ein paar Angaben von dir.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1">
                    Sozialversicherungsnummer (SVNR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" value={svnr} onChange={e => setSvnr(e.target.value)}
                    placeholder="1234 010190"
                    className="w-full px-3 py-2.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[#1A1A2E] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]"
                  />
                  <p className="text-xs text-[#9CA3AF] mt-1">10-stellige Nummer auf deiner e-Card</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1">
                    IBAN <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" value={iban} onChange={e => setIban(e.target.value)}
                    placeholder="AT12 3456 7890 1234 5678"
                    className="w-full px-3 py-2.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[#1A1A2E] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1">
                    Adresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" value={adresse} onChange={e => setAdresse(e.target.value)}
                    placeholder="Straße Nr., PLZ Ort"
                    className="w-full px-3 py-2.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[#1A1A2E] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]"
                  />
                </div>

                {/* #948 — Staatsbürgerschaft (Pflicht) */}
                <div>
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1">
                    Staatsbürgerschaft <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={staatsbuergerschaft}
                    onChange={e => setStaatsbuergerschaft(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[#1A1A2E] bg-white focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]"
                  >
                    <option value="">— bitte auswählen —</option>
                    {COUNTRIES_SORTED.map(c => (
                      <option key={c.code} value={c.code}>{c.de}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1">
                    Ausweiskopie (Reisepass oder Personalausweis)
                  </label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="w-full px-3 py-4 rounded-lg border border-dashed border-[#D1D5DB] bg-[#F9FAFB] text-center cursor-pointer hover:border-[#338edc] transition-colors"
                  >
                    {ausweisFile ? (
                      <span className="text-sm text-[#1A1A2E] font-medium">{ausweisFile.name}</span>
                    ) : (
                      <span className="text-sm text-[#9CA3AF]">JPG, PNG oder PDF — klicken zum Hochladen</span>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf"
                    onChange={e => setAusweisFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </div>

                {/* #948 — Aufenthaltstitel-Upload bei Drittstaat (Pflicht, §53 AuslBG) */}
                {drittstaat && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <label className="block text-sm font-medium text-[#1A1A2E] mb-1">
                      Aufenthaltstitel / Arbeitsberechtigung <span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-[#6B7280] mb-2 leading-relaxed">
                      Für die Staatsbürgerschaft <strong>{getCountryName(staatsbuergerschaft)}</strong> benötigen wir einen
                      gültigen Aufenthaltstitel bzw. Nachweis der Arbeitsberechtigung in Österreich.
                    </p>
                    <div
                      onClick={() => aufenthaltRef.current?.click()}
                      className="w-full px-3 py-4 rounded-lg border border-dashed border-amber-300 bg-white text-center cursor-pointer hover:border-amber-500 transition-colors"
                    >
                      {aufenthaltstitelFile ? (
                        <span className="text-sm text-[#1A1A2E] font-medium">{aufenthaltstitelFile.name}</span>
                      ) : hasAufenthaltstitelOnServer ? (
                        <span className="text-sm text-emerald-700">&#10003; Bereits hochgeladen — neu wählen, um zu ersetzen</span>
                      ) : (
                        <span className="text-sm text-amber-700">JPG, PNG oder PDF — klicken zum Hochladen</span>
                      )}
                    </div>
                    <input ref={aufenthaltRef} type="file" accept=".jpg,.jpeg,.png,.pdf"
                      onChange={e => setAufenthaltstitelFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                  </div>
                )}

                <button type="submit" disabled={submitting}
                  className="w-full py-3.5 rounded-xl bg-[#338edc] hover:bg-[#2b7bc4] text-white font-semibold text-base disabled:opacity-50 transition-colors mt-2"
                >
                  {submitting ? '...' : 'Daten absenden'}
                </button>
              </form>
            </div>
          )}

          {pageStatus === 'completed' && (
            <div className="bg-white rounded-2xl border border-[var(--border-default,#E5E7EB)] p-8 text-center">
              <div className="text-4xl mb-4">&#9989;</div>
              <h2 className="text-lg font-semibold text-[#065F46] mb-2">Daten erfolgreich übermittelt!</h2>
              <p className="text-sm text-[#047857]">
                Vielen Dank{data?.bewerber_name ? `, ${data.bewerber_name.split(' ')[0]}` : ''}. Wir melden uns bei dir.
              </p>
            </div>
          )}

          <div className="text-center mt-8 text-xs text-[#9CA3AF] space-y-1">
            <p>Fragen? jobs@email.weship.eu</p>
            <p>&copy; 2026 We Ship Fulfillment GmbH</p>
          </div>
        </div>
      </div>
    </div>
  );
}
